import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Test } from '../../types/test';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/Modal';
import './TestCreation.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Predefined topics for HR tests
const TEST_TOPICS = [
  'HR Policies',
  'Employee Handbook',
  'Safety Procedures',
  'Onboarding Process',
  'Disciplinary Procedures',
  'Benefits & Compensation',
  'Code of Conduct',
  'Workplace Safety',
  'Leave Policies',
  'Performance Management',
  'Data Privacy & Security',
  'Harassment Prevention',
  'Remote Work Policies',
  'Diversity & Inclusion',
];

const TestCreation: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tests, setTests] = useState<Test[]>([]);
  const [collections, setCollections] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [testRequest, setTestRequest] = useState({
    topic: '',
    description: '',
    numQuestions: 5,
    difficulty: 'medium',
    collection_name: '',
  });
  const [showCustomTopic, setShowCustomTopic] = useState(false);
  
  // Modal state
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning' | 'loading';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  useEffect(() => {
    fetchTests();
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await fetch(`${API_URL}/collections`);
      const data = await response.json();
      const collectionNames = data.collections || [];
      setCollections(collectionNames);
      // Always set collection if not already set or if current selection is invalid
      if (collectionNames.length > 0) {
        setTestRequest(prev => {
          // Keep current selection if it's valid, otherwise use first available
          const currentValid = collectionNames.includes(prev.collection_name);
          return {
            ...prev,
            collection_name: currentValid ? prev.collection_name : collectionNames[0],
          };
        });
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  };

  const fetchTests = async () => {
    try {
      const response = await fetch(`${API_URL}/tests`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTests(data.tests || []);
      }
    } catch (error) {
      console.error('Error fetching tests:', error);
    }
  };

  const handleCreateTest = async () => {
    if (!testRequest.topic.trim()) {
      setModal({
        isOpen: true,
        title: 'Missing Topic',
        message: 'Please select a test topic',
        type: 'warning',
      });
      return;
    }

    if (!testRequest.collection_name) {
      setModal({
        isOpen: true,
        title: 'Missing Collection',
        message: 'Please select a document collection',
        type: 'warning',
      });
      return;
    }

    setLoading(true);
    
    // Show loading modal
    setModal({
      isOpen: true,
      title: 'Creating Test',
      message: 'Generating questions from your documents. This may take a moment...',
      type: 'loading',
    });
    
    try {
      const response = await fetch(`${API_URL}/tests/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          ...testRequest,
          createdBy: user?.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create test' }));
        throw new Error(errorData.detail || 'Failed to create test');
      }

      const data = await response.json();
      
      // Close loading modal and show success
      setModal({
        isOpen: true,
        title: 'Success',
        message: 'Test created successfully! Redirecting to test editor...',
        type: 'success',
      });
      
      // Navigate after modal confirmation
      setTimeout(() => {
        navigate(`/admin/tests/${data.test.id}/edit`);
      }, 1000);
    } catch (error) {
      console.error('Error creating test:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create test. Please try again.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="test-creation">
      <Modal
        isOpen={modal.isOpen}
        onClose={modal.type === 'loading' ? undefined : () => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        isLoading={modal.type === 'loading'}
      />
      
      <div className="page-header">
        <button className="button-back" onClick={() => navigate('/admin/dashboard')}>
          ← Back to Dashboard
        </button>
        <div>
          <h1>Test Creation</h1>
          <p>Create new tests with AI assistance or manage existing tests</p>
        </div>
        <button
          className="button-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : '+ Create New Test'}
        </button>
      </div>

      {showCreateForm && (
        <div className="create-form-card">
          <h2>Ask for Test Creation</h2>
          <div className="form-group">
            <label>Test Topic/Subject *</label>
            <div className="topic-input-container">
              <select
                value={showCustomTopic ? 'custom' : (TEST_TOPICS.includes(testRequest.topic) ? testRequest.topic : (testRequest.topic ? 'custom' : ''))}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setShowCustomTopic(true);
                    if (TEST_TOPICS.includes(testRequest.topic)) {
                      setTestRequest({ ...testRequest, topic: '' });
                    }
                  } else if (e.target.value) {
                    setShowCustomTopic(false);
                    setTestRequest({ ...testRequest, topic: e.target.value });
                  } else {
                    setShowCustomTopic(false);
                    setTestRequest({ ...testRequest, topic: '' });
                  }
                }}
                className="topic-select"
              >
                <option value="">Select a topic...</option>
                {TEST_TOPICS.map((topic) => (
                  <option key={topic} value={topic}>
                    {topic}
                  </option>
                ))}
                <option value="custom">Custom Topic...</option>
              </select>
              {(showCustomTopic || (!TEST_TOPICS.includes(testRequest.topic) && testRequest.topic !== '')) && (
                <input
                  type="text"
                  className="topic-custom-input"
                  value={testRequest.topic}
                  onChange={(e) => {
                    setTestRequest({ ...testRequest, topic: e.target.value });
                    if (TEST_TOPICS.includes(e.target.value)) {
                      setShowCustomTopic(false);
                    } else {
                      setShowCustomTopic(true);
                    }
                  }}
                  placeholder="Enter custom topic..."
                />
              )}
            </div>
            <p className="form-help-text">
              Questions will be generated from documents based on this topic
            </p>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={testRequest.description}
              onChange={(e) => setTestRequest({ ...testRequest, description: e.target.value })}
              placeholder="Additional context or requirements for the test..."
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Document Collection *</label>
            <select
              value={testRequest.collection_name}
              onChange={(e) => setTestRequest({ ...testRequest, collection_name: e.target.value })}
            >
              {collections.length === 0 ? (
                <option value="">Loading collections...</option>
              ) : (
                collections.map((collection) => (
                  <option key={collection} value={collection}>
                    {collection}
                  </option>
                ))
              )}
            </select>
            <p className="form-help-text">
              Questions will be generated based on documents in this collection
            </p>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Number of Questions</label>
              <select
                value={testRequest.numQuestions}
                onChange={(e) => setTestRequest({ ...testRequest, numQuestions: parseInt(e.target.value) })}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
              </select>
            </div>
            <div className="form-group">
              <label>Difficulty</label>
              <select
                value={testRequest.difficulty}
                onChange={(e) => setTestRequest({ ...testRequest, difficulty: e.target.value })}
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>
          <button
            className="button-primary"
            onClick={handleCreateTest}
            disabled={loading || !testRequest.collection_name || !testRequest.topic}
          >
            {loading ? 'Creating Test from Documents...' : 'Generate Test from Documents'}
          </button>
          {(!testRequest.collection_name || !testRequest.topic) && (
            <p className="form-error-text">
              {!testRequest.topic ? 'Please select a test topic' : 'Please select a document collection to generate questions from'}
            </p>
          )}
        </div>
      )}

      <div className="tests-list">
        <h2>Existing Tests</h2>
        {tests.length === 0 ? (
          <div className="empty-state">
            <p>No tests created yet. Create your first test above!</p>
          </div>
        ) : (
          <div className="tests-grid">
            {tests.map((test) => (
              <div
                key={test.id}
                className="test-card"
                onClick={() => navigate(`/admin/tests/${test.id}/edit`)}
              >
                <div className="test-card-header">
                  <h3>{test.title}</h3>
                  <span className={`test-status ${test.published ? 'published' : 'draft'}`}>
                    {test.published ? 'Published' : 'Draft'}
                  </span>
                </div>
                <p className="test-description">{test.description}</p>
                <div className="test-meta">
                  <span>📝 {test.questions.length} Questions</span>
                  <span>📅 {new Date(test.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TestCreation;
